import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Box,
  Badge,
} from "@mui/material";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";

import { getStyles } from "src/styles/theme";
import defaultStyles from "./styles";

export type DashBoardTable = {
  topHeader: string[];
  data: any[];
  editable?: boolean;
  showPlantedSeedlings?: boolean;
  onChangePlanted?: (id: string, value: number) => void;
  onQueryClick?: (row: any) => void;
};

export function SeedlingTable({
  data,
  topHeader,
  editable = false,
  showPlantedSeedlings = true,
  onChangePlanted,
  onQueryClick,
}: DashBoardTable) {
  const styles = getStyles(defaultStyles);

  const totalSeedlings = data.reduce(
    (sum, item) => sum + (item.numberOfSeedlings || 0),
    0
  );

  const totalPlantedSeedlings = data.reduce(
    (sum, item) => sum + (item.numberOfPlantedSeedlings || 0),
    0
  );

  const dateFormatter = (date: string) => {
    if (!date) return "-";
    return new Date(date.split("T")[0]).toLocaleDateString("en-GB");
  };

  return (
    <TableContainer component={Paper} sx={{ height: "fit-content" }}>
      <Table sx={{ minWidth: 600 }} aria-label="simple table">
        <TableHead {...styles("header")}>
          <TableRow>
            {topHeader.map((item, index) => (
              <TableCell align="center" {...styles("cell")} key={index}>
                {item}
              </TableCell>
            ))}
            {onQueryClick && (
              <TableCell align="center" {...styles("cell")}>
                Query
              </TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((seedData, index) => {
            if (!seedData) return null;
            return (
              <TableRow key={index}>
                <TableCell
                  align="center"
                  {...styles("cell", { width: "100px" })}
                >
                  {seedData.commonName}
                </TableCell>
                <TableCell
                  align="center"
                  {...styles("cell", { width: "100px" })}
                >
                  {seedData.botanicalName}
                </TableCell>
                <TableCell
                  align="center"
                  {...styles("cell", { width: "100px" })}
                >
                  {seedData.numberOfSeedlings}
                </TableCell>
                {showPlantedSeedlings && (
                  <TableCell
                    align="center"
                    {...styles("cell", { width: "120px" })}
                  >
                    {editable ? (
                      <TextField
                        type="number"
                        size="small"
                        value={seedData.numberOfPlantedSeedlings ?? 0}
                        onChange={(event) => {
                          const value = event.target.value;
                          const numericValue = value === "" ? 0 : Number(value);
                          if (onChangePlanted && seedData.id) {
                            onChangePlanted(seedData.id, numericValue);
                          }
                        }}
                        inputProps={{
                          min: 0,
                          style: { textAlign: "center" },
                        }}
                        sx={{ width: "100%" }}
                      />
                    ) : (
                      seedData.numberOfPlantedSeedlings ?? 0
                    )}
                  </TableCell>
                )}
                {showPlantedSeedlings && (
                  <>
                    <TableCell
                      align="center"
                      {...styles("cell", { width: "120px" })}
                    >
                      {dateFormatter(seedData.fromDate)}
                    </TableCell>
                    <TableCell
                      align="center"
                      {...styles("cell", { width: "120px" })}
                    >
                      {dateFormatter(seedData.toDate)}
                    </TableCell>
                  </>
                )}
                {onQueryClick && (
                  <TableCell
                    align="center"
                    {...styles("cell", { width: "80px" })}
                  >
                    <Box
                      onClick={() => onQueryClick(seedData)}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        padding: "4px",
                        borderRadius: "4px",
                        "&:hover": {
                          backgroundColor: "#e3f2fd",
                        },
                      }}
                    >
                      <Badge
                        badgeContent={seedData.pendingQueryCount || 0}
                        color="error"
                        max={99}
                        sx={{
                          "& .MuiBadge-badge": {
                            fontSize: "10px",
                            height: "16px",
                            minWidth: "16px",
                            padding: "0 3px",
                          },
                        }}
                      >
                        <ChatBubbleOutlineIcon
                          fontSize="small"
                          sx={{
                            color:
                              seedData.pendingQueryCount > 0
                                ? "#d32f2f"
                                : "#2196f3",
                          }}
                        />
                      </Badge>
                    </Box>
                  </TableCell>
                )}
              </TableRow>
            );
          })}
          <TableRow>
            <TableCell></TableCell>
            <TableCell align="center">Total Number of Seedlings: </TableCell>
            <TableCell align="center">
              <strong>{totalSeedlings}</strong>{" "}
            </TableCell>
          </TableRow>
          {showPlantedSeedlings && (
            <TableRow>
              <TableCell></TableCell>
              <TableCell align="center">
                Total Number of Planted Seedlings:{" "}
              </TableCell>
              <TableCell align="center">
                <strong>{totalPlantedSeedlings}</strong>{" "}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
